import React, { useEffect, useState } from 'react';
import validator from 'validator';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import { DateTime } from 'luxon';
import Stack from 'react-bootstrap/Stack';
import Container from 'react-bootstrap/Container';
import { useAuth } from '../../providers/auth-provider';
import Modal from 'react-bootstrap/Modal';
import { getClientConfiguration } from '../../api/client';
import { getMetadataAttributes, getMetadataUniqueValues, getUserInfoAttributeId } from '../../api/metadata';
import { launch } from '../../api/assessments-api';
import { PRODUCT_CLASSIFICATION } from '../../utils/constants';
import LoadingSpinner from '../common/loading-spinner';

function LaunchIndividualContent({ selectionMade }) {
  const auth = useAuth();
  const { isSelect } = useAuth();
  const [dropdowns, setDropdowns] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [assessmentType, setAssessmentType] = useState(0);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isRaterAssessment, setIsRaterAssessment] = useState(false);
  const [assessmentOfEmail, setAssessmentOfEmail] = useState('');
  const [newOption, setNewOption] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showLaunchMessage, setShowLaunchMessage] = useState(false);
  const [launchMessage, setLaunchMessage] = useState('');
  const [launchMessageVariant, setLaunchMessageVariant] = useState('success');
  const [loading, setLoading] = useState(false);
  const [expirationDate, setExpirationDate] = useState(DateTime.now().plus({ weeks: 2 }).toFormat('yyyy-MM-dd'));
  const [expirationDateError, setExpirationDateError] = useState('');

  const isEmailValid = (email) => {
    return validator.isEmail(email);
  };

  const handleAssessmentTypeChange = (event) => {
    setAssessmentType(event.target.value);
    if (assessments[event.target.value].isRaterAssessment) {
      setIsRaterAssessment(true);
    } else {
      setIsRaterAssessment(false);
    }
  };

  const handleSelectChange = (event, dropdownAttribute) => {
    setDropdowns((prevDropdowns) =>
      prevDropdowns.map((dropdown) =>
        dropdown.attribute === dropdownAttribute ? { ...dropdown, selectedOption: event.target.value } : dropdown
      )
    );
  };

  const handleAddOption = () => {
    setDropdowns((prevDropdowns) =>
      prevDropdowns.map((dropdown) =>
        dropdown.attribute === activeDropdown
          ? { ...dropdown, options: [...dropdown.options, newOption], selectedOption: newOption }
          : dropdown
      )
    );
    setNewOption('');
    setShowModal(false);
  };

  const handleShowModal = (dropdownAttribute) => {
    setActiveDropdown(dropdownAttribute);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleInputChange = (event, setter) => {
    setter(event.target.value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    const launchData = {
      name: name,
      email: email,
      assessment_ids: assessments[assessmentType].assessmentIds,
      product_classification: isSelect() ? PRODUCT_CLASSIFICATION.select : PRODUCT_CLASSIFICATION.develop,
      expires: expirationDate,
    };
    if (assessmentOfEmail?.length > 0) {
      launchData.assessment_of_email = assessmentOfEmail;
    }

    launchData.metadata = dropdowns
      .filter((dropdown) => dropdown.selectedOption != null && dropdown.selectedOption !== '')
      .map((dropdown) => ({
        attribute: dropdown.attribute,
        metadata: { value: dropdown.selectedOption },
      }));

    const userInfoAttributeId = await getUserInfoAttributeId(auth);
    const userInfoMetadata = { target: true };
    if (isSelect()) {
      userInfoMetadata.select = true;
    }
    launchData.metadata.push({
      attribute: userInfoAttributeId,
      metadata: userInfoMetadata,
    });

    try {
      const response = await launch(auth, launchData);
      setLoading(false);
      if (response.error) {
        setLaunchMessageVariant('danger');
        setLaunchMessage(`An error occurred: ${response.error}`);
        setShowLaunchMessage(true);
      } else if (response.id === -1) {
        setLaunchMessageVariant('danger');
        setShowLaunchMessage(true);
        setLaunchMessage(`A user with the email address "${response.email}" already exists. Invitation was not sent.`);
      } else {
        setLaunchMessageVariant('success');
        setShowLaunchMessage(true);
        setLaunchMessage(
          `Assessment launched successfully with user name "${response.email}" and password "${response.password}".`
        );
        setName('');
        setEmail('');
        setAssessmentOfEmail('');
        setDropdowns(dropdowns.map((dropdown) => ({ ...dropdown, selectedOption: '' })));
      }
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    const config = getClientConfiguration();
    const assessments = [];
    const metadataAttributes = [];
    const metaDataValues = [];
    if (isSelect()) {
      config.select.launch.assessments.forEach((assessment) => {
        assessments.push(assessment);
      });
      metadataAttributes.push(...config.select.launch.metadata);
    } else {
      config.develop.launch.assessments.forEach((assessment) => {
        assessments.push(assessment);
      });
      metadataAttributes.push(...config.develop.launch.metadata);
    }

    getMetadataAttributes(auth).then((allAttributes) => {
      const launchMetadata = isSelect() ? config.select.launch.metadata : config.develop.launch.metadata;
      for (const attributeId of launchMetadata) {
        metaDataValues.push({
          attribute: attributeId,
          title: allAttributes[attributeId].displayName,
          options: [],
          selectedOption: '',
        });
      }
      getMetadataUniqueValues(auth, metadataAttributes).then((valueSet) => {
        for (const attribute of valueSet) {
          for (const metadataValue of metaDataValues) {
            if (metadataValue.attribute === attribute.attributeId) {
              metadataValue.options = attribute.values;
              break;
            }
          }
        }
        setAssessments(assessments);
        setIsRaterAssessment(assessments[0].isRaterAssessment);
        setDropdowns(metaDataValues);
      });
    });
  }, []);

  const handleExpirationDateChange = (e) => {
    const selectedDate = e.target.value;
    const tomorrow = DateTime.now().plus({ days: 1 }).startOf('day');
    const selectedDateTime = DateTime.fromISO(selectedDate);

    if (selectedDateTime < tomorrow) {
      setExpirationDateError('Expiration date must be at least one day after today');
    } else {
      setExpirationDateError('');
      setExpirationDate(selectedDate);
    }
  };

  return (
    <>
      {loading && <LoadingSpinner />}
      <Stack className="w-100 p-4 d-flex">
        <Container className="w-75 w-100" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {showLaunchMessage && (
            <Alert variant={launchMessageVariant} onClose={() => setShowLaunchMessage(false)} dismissible>
              {launchMessage}
            </Alert>
          )}
          <Form onSubmit={handleSubmit}>
            <Form.Group as={Row} className="mb-3" controlId="formBasicAssessmentType">
              <Form.Label column sm={3}>
                Assessment Type
              </Form.Label>
              <Col sm={9}>
                <Form.Select value={assessmentType} onChange={handleAssessmentTypeChange}>
                  {assessments.map((assessment, index) => (
                    <option key={index} value={index}>
                      {assessment.title}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3" controlId="formBasicName">
              <Form.Label column sm={3}>
                {`${isRaterAssessment ? 'Rater' : 'Target'} Name`}
              </Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="text"
                  placeholder={`Enter ${isRaterAssessment ? 'rater' : 'target'} name`}
                  value={name}
                  onBlur={(e) => handleInputChange(e, setName)}
                  onChange={(e) => handleInputChange(e, setName)}
                />
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3" controlId="formBasicEmail">
              <Form.Label column sm={3}>
                {`${isRaterAssessment ? 'Rater' : 'Target'} Email Address`}
              </Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="email"
                  placeholder={`Enter ${isRaterAssessment ? 'rater' : 'target'} email address`}
                  value={email}
                  onBlur={(e) => handleInputChange(e, setEmail)}
                  onChange={(e) => handleInputChange(e, setEmail)}
                  className={!isEmailValid(email) && 'form-input-invalid-value'}
                />
              </Col>
            </Form.Group>
            {isRaterAssessment && (
              <Form.Group as={Row} className="mb-3" controlId="formRaterEmail">
                <Form.Label column sm={3}>
                  Email Address of the Target
                </Form.Label>
                <Col sm={9}>
                  <Form.Control
                    type="email"
                    placeholder="Enter email address of the target"
                    value={assessmentOfEmail}
                    onBlur={(e) => handleInputChange(e, setAssessmentOfEmail)}
                    onChange={(e) => handleInputChange(e, setAssessmentOfEmail)}
                    className={!isEmailValid(email) && 'form-input-invalid-value'}
                  />
                </Col>
              </Form.Group>
            )}

            {!isRaterAssessment && (
              <>
                {dropdowns.map((dropdown) => (
                  <div key={dropdown.attribute}>
                    <Form.Group as={Row} className="mb-3">
                      <Form.Label column sm={3}>
                        {dropdown.title}
                      </Form.Label>
                      <Col sm={7}>
                        <Form.Select
                          value={dropdown.selectedOption}
                          onChange={(e) => handleSelectChange(e, dropdown.attribute)}
                        >
                          <option value="">Select...</option>
                          {dropdown.options.map((option, index) => (
                            <option key={index} value={option}>
                              {option}
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col sm={2} className="d-flex justify-content-end">
                        <Button
                          style={{ whiteSpace: 'nowrap', textTransform: 'none' }}
                          onClick={() => handleShowModal(dropdown.attribute)}
                        >
                          Add {dropdown.title}
                        </Button>
                      </Col>
                    </Form.Group>
                  </div>
                ))}
              </>
            )}
            <Form.Group as={Row} className="mb-3" controlId="formBasicExpiration">
              <Form.Label column sm={3}>
                Assessment Expiration
              </Form.Label>
              <Col sm={2}>
                <Form.Control
                  type="date"
                  value={expirationDate}
                  onChange={handleExpirationDateChange}
                  min={DateTime.now().plus({ days: 1 }).toFormat('yyyy-MM-dd')}
                  isInvalid={!!expirationDateError}
                />
                {expirationDateError && (
                  <Form.Control.Feedback type="invalid">{expirationDateError}</Form.Control.Feedback>
                )}
              </Col>
            </Form.Group>

            <div className="d-flex justify-content-center mt-3">
              <Button
                variant="primary"
                type="submit"
                disabled={!name || !isEmailValid(email) || (isRaterAssessment && !assessmentOfEmail)}
              >
                Send Invitation
              </Button>
            </div>

            <div className="d-flex justify-content-center mt-3">
              <Button variant="secondary" style={{ textTransform: 'none' }} onClick={() => selectionMade('back')}>
                Return to Launch Page
              </Button>
            </div>
          </Form>
        </Container>
        <Modal show={showModal} onHide={handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>
              Add New {dropdowns.find((dropdown) => dropdown.attribute === activeDropdown)?.title}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Control
              type="text"
              placeholder={`Enter new ${dropdowns
                .find((dropdown) => dropdown.attribute === activeDropdown)
                ?.title.toLowerCase()}`}
              value={newOption}
              onChange={(e) => setNewOption(e.target.value)}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAddOption}>
              Save Changes
            </Button>
          </Modal.Footer>
        </Modal>
      </Stack>
    </>
  );
}

export default LaunchIndividualContent;
